/* eslint-disable no-use-before-define */
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadQuote from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuote";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { blackBlueBtn } from "@tKgpco/Extensions/Components/WishListCardExtension";
import getSecondarySalesPerson from "@tKgpco/Handlers/RfqQuoteSecondarySalesPerson";
import secondarySalesPersonAssign from "@tKgpco/Handlers/RfqQuoteSecondarySalesPersonAssign";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import React, { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
    const { search } = getLocation(state);
    const parsedQuery = parseQueryString<{ quoteId?: string }>(search);
    const quoteId = parsedQuery.quoteId;
    return {
        quoteState: getQuoteState(state, state.pages.rfqQuoteDetails.quoteId),
        rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
        cartState: getCurrentCartState(state),
        userName: state.context.session.userName,
        quoteId
    }
};

const mapDispatchToProps = {
    getSecondarySalesPerson,
    secondarySalesPersonAssign,
    loadQuote
};

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteSalesPersonSelectionStyles {
    selectGrid?: GridItemProps;
    buttonGridItem?: GridItemProps;
    title: TypographyProps;
    saveBtn?: ButtonPresentationProps;
}

export const rfqQuoteSalesPersonSelectionStyles: RfqQuoteSalesPersonSelectionStyles = {
    selectGrid: {
        width: 6,
        css: css`
            padding: 18px 15px;
        `
    },
    buttonGridItem: {
        css: css`
            
        `
    },
    title: {
        css: css`
            padding: 10px 15px
        `,
    },
    saveBtn: {
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            margin: 27px 0px !important;
            text-transform: uppercase;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            min-width: 120px;
            background: ${COLORS.white};
            color: ${COLORS.secondaryBtn};
            & :hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
            }
            &:disabled {
                background: ${COLORS.disabled.base};
                color: ${COLORS.white};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
                border: 1px solid ${COLORS.disabled.base};
                color: ${COLORS.white};
            }
        `,
    }
};

const styles = rfqQuoteSalesPersonSelectionStyles;

const RfqQuoteSalesPersonSelection: FC<Props> = ({
    quoteState,
    getSecondarySalesPerson,
    secondarySalesPersonAssign,
    userName,
    quoteId,
    loadQuote
}) => {
    const [secondarySalesPerson, setSecondarySalesPerson] = useState<string[]>([]);
    const [selectedSalesPerson, setSelectedSalesPerson] = useState<string>("");
    const toasterContext = useContext(ToasterContext);

    useEffect(() => {
        if(quoteState.value?.properties?.secondarySalesRep) {
            setSelectedSalesPerson(quoteState.value?.properties?.secondarySalesRep)
        }
    }, [quoteState.value?.properties]);

    useEffect(() => {
        if(quoteState.value) {
            getSecondarySalesPerson({ 
                id: quoteState.value.customerNumber,
                onSuccess(result: any) {
                    if(result) {
                        setSecondarySalesPerson(result);
                    }
                },
            });
        } 
    },[quoteState]);

    if (!quoteState.value) {
        return null;
    }

    const quote = quoteState.value;

    const userChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setSelectedSalesPerson(event.target.value);
    }
    const assignSalesPerson = () => {
        secondarySalesPersonAssign({
            SecondarySalesRep: selectedSalesPerson,
            QuoteNumber: quote.quoteNumber,
            onSuccess() {
                toasterContext.addToast({ body: translate("Secondary sales person updated successfully."), messageType: "success" });
                quoteId && loadQuote({ quoteId });
            }
        })
    }
    const quoteStatus = ["QuoteAccepted", "QuoteProposed"]
    const isLoadDropdown = !quote.isSalesperson || quoteStatus.includes(quote.status) ||  quoteState.value?.properties?.showSecondarySalesRepDropdown === "false";
    
    return (
        <GridContainer>
            {quoteState.value?.properties?.primarySalesRep ? <Typography {...styles.title} as="h2">{`Sales Person assigned by ${quoteState.value?.properties?.primarySalesRep}`}</Typography>: null}
                
                {isLoadDropdown ? null:
                <>
                    <GridItem {...styles.selectGrid}>
                        <Select
                            label={translate("Secondary Sales Person")}
                            value={selectedSalesPerson}
                            onChange={userChangeHandler}
                            data-test-selector="requestQuoteAssignUserSelect"
                        >
                            <option key="" value="">
                                {translate("Select Secondary Sales Person")}
                            </option>
                            {secondarySalesPerson.filter((user) => user !== userName).map((o, i) => {
                                return (
                                    <option key={o} value={o}>
                                        {o}
                                    </option>
                                );
                            })}
                        </Select>
                    </GridItem>
                    <GridItem {...styles.selectGrid}>
                        <Button
                            {...styles.saveBtn}
                            onClick={assignSalesPerson}
                        >
                            {translate("Save")}
                        </Button>
                    </GridItem>
                </>
                }
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqQuoteSalesPersonSelection)),
    definition: {
        displayName: "Sales Person Selection",
        allowedContexts: ["RfqQuoteDetailsPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Details",
    },
};

export default widgetModule;